import React from 'react';
import { ArrowLeft, Shield, Mail, Phone, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">Back to Home</span>
              </a>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Privacy Policy</h1>
                <p className="text-xs text-gray-600">VV CosmoTech</p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = '/contact'}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your information.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            <strong>Effective Date:</strong> 31st March 2025
          </p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-10 p-6 bg-gray-50 rounded-xl">
              <p className="text-gray-700 leading-relaxed">
                Welcome to <strong>VV CosmoTech</strong> ("Company", "we", "our", or "us"), operating under <strong>Vijan Ventures LLP</strong>. We value your trust and are committed to protecting your personal information and maintaining transparency in how we collect, use, and safeguard your data.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                This Privacy Policy explains how VV CosmoTech collects, uses, stores, and protects information when you visit our website, communicate with us, or engage with our services.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4 font-medium">
                By accessing or using our website, you agree to the terms outlined in this Privacy Policy.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                Information We Collect
              </h3>
              <p className="text-gray-700 mb-4">We may collect the following categories of information:</p>
              
              <div className="ml-4 space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">A. Personal Information</h4>
                  <p className="text-gray-700 mb-2">Information voluntarily provided by you, including:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Name</li>
                    <li>Company Name</li>
                    <li>Email Address</li>
                    <li>Phone Number</li>
                    <li>GST Number</li>
                    <li>Business Details</li>
                    <li>Billing & Shipping Information</li>
                    <li>Product Requirements & Manufacturing Queries</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">B. Business & Project Information</h4>
                  <p className="text-gray-700 mb-2">When you enquire about our services, we may collect:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Product categories</li>
                    <li>Formulation requirements</li>
                    <li>Packaging requirements</li>
                    <li>Estimated order quantities</li>
                    <li>Manufacturing specifications</li>
                    <li>Brand and project-related details</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">C. Website Usage Information</h4>
                  <p className="text-gray-700 mb-2">We may automatically collect certain information through cookies and analytics tools, including:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>IP address</li>
                    <li>Browser type</li>
                    <li>Device information</li>
                    <li>Pages visited</li>
                    <li>Time spent on website</li>
                    <li>Referring URLs</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                How We Use Your Information
              </h3>
              <p className="text-gray-700 mb-4">We use the information collected for the following purposes:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>To respond to enquiries and consultation requests</li>
                <li>To provide manufacturing, formulation, packaging, and related services</li>
                <li>To prepare quotations, proposals, and project documentation</li>
                <li>To improve our website, services, and customer experience</li>
                <li>To communicate important updates regarding projects or services</li>
                <li>To conduct internal analysis and business operations</li>
                <li>To comply with legal and regulatory obligations</li>
                <li>To send relevant business communications, promotional material, or service updates (where permitted)</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                Confidentiality of Product Information
              </h3>
              <p className="text-gray-700 mb-4">
                VV CosmoTech understands the importance of confidentiality in product development and manufacturing.
              </p>
              <p className="text-gray-700 mb-4">
                Any formulation concepts, product briefs, packaging ideas, or project-related information shared with us during discussions or collaborations will be treated with professional discretion.
              </p>
              <p className="text-gray-700">
                Where applicable, separate Non-Disclosure Agreements (NDAs) may be executed between parties.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                Sharing of Information
              </h3>
              <p className="text-gray-700 mb-4 font-medium">
                We do not sell, rent, or trade your personal information to third parties.
              </p>
              <p className="text-gray-700 mb-4">We may share information only in the following situations:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>With trusted service providers or partners involved in operational support</li>
                <li>With logistics, compliance, or packaging partners where necessary for project execution</li>
                <li>If required by law, regulation, or legal process</li>
                <li>To protect the rights, safety, and operations of VV CosmoTech</li>
              </ul>
              <p className="text-gray-700 mt-4">
                All such sharing is conducted only to the extent necessary.
              </p>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                Data Security
              </h3>
              <p className="text-gray-700 mb-4">
                We implement commercially reasonable security measures to protect your information against unauthorized access, misuse, disclosure, or loss.
              </p>
              <p className="text-gray-700">
                While we strive to use industry-standard safeguards, no method of transmission over the internet or electronic storage is completely secure. Therefore, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">6</span>
                Cookies & Analytics
              </h3>
              <p className="text-gray-700 mb-4">
                Our website may use cookies and similar technologies to enhance user experience and improve website performance.
              </p>
              <p className="text-gray-700 mb-4">Cookies help us:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Understand website traffic and user behavior</li>
                <li>Improve website functionality</li>
                <li>Optimize marketing and advertising campaigns</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You may choose to disable cookies through your browser settings.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">7</span>
                Third-Party Links
              </h3>
              <p className="text-gray-700 mb-4">
                Our website may contain links to external websites or third-party services. VV CosmoTech is not responsible for the privacy practices, policies, or content of such third-party websites.
              </p>
              <p className="text-gray-700">
                Users are encouraged to review the respective privacy policies of external websites before sharing information.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">8</span>
                Data Retention
              </h3>
              <p className="text-gray-700 mb-4">We retain personal and business information only for as long as necessary to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Fulfill business purposes</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Maintain internal records</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We reserve the right to securely delete information that is no longer required.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">9</span>
                Your Rights
              </h3>
              <p className="text-gray-700 mb-4">Depending on applicable laws and regulations, you may have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Request access to your information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for communications</li>
                <li>Request limitation of data processing</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise any of these rights, please contact us using the details below.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">10</span>
                Intellectual Property & Website Content
              </h3>
              <p className="text-gray-700 mb-4">
                All website content, including text, graphics, branding, images, logos, and materials, are the intellectual property of VV CosmoTech and/or Vijan Ventures LLP unless otherwise stated.
              </p>
              <p className="text-gray-700">
                Unauthorized reproduction, distribution, or use of website content is prohibited.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">11</span>
                Policy Updates
              </h3>
              <p className="text-gray-700 mb-4">
                VV CosmoTech reserves the right to update or modify this Privacy Policy at any time without prior notice.
              </p>
              <p className="text-gray-700 mb-4">
                Any updates will be reflected on this page along with the revised effective date.
              </p>
              <p className="text-gray-700">
                Continued use of the website following updates constitutes acceptance of the revised policy.
              </p>
            </div>

            {/* Section 12 - Contact */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">12</span>
                Contact Us
              </h3>
              <p className="text-gray-700 mb-4">
                For any questions, concerns, or requests related to this Privacy Policy, please contact:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">VV CosmoTech</h4>
                <p className="text-gray-700 mb-4">Under Vijan Ventures LLP</p>
                <div className="space-y-3">
                  <a href="mailto:info@vvcosmotech.com" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span>info@vvcosmotech.com</span>
                  </a>
                  <a href="tel:+919653226520" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span>+91 9653226520</span>
                  </a>
                  <a href="https://www.vvcosmotech.com" className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span>www.vvcosmotech.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Disclaimer</h3>
              <p className="text-gray-700 mb-4">
                The information provided on this website is intended for general informational and business purposes only. Product development timelines, formulation results, packaging availability, and manufacturing specifications may vary depending on project requirements and operational factors.
              </p>
              <p className="text-gray-700">
                VV CosmoTech reserves the right to refuse service, revise project scopes, or modify operational policies at its discretion.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-gray-400 mb-4">
            &copy; {new Date().getFullYear()} VV CosmoTech. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">Home</a>
            <a href="/products" className="text-gray-400 hover:text-white transition-colors">Products</a>
            <a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300 transition-colors">Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
